import { FC, ReactNode, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Skeleton, Stack } from '@mui/material';

import dayjs from 'dayjs';

import { PickupPointView, TariffQuote } from '@/api/data-contracts';
import { ReactComponent as ArrowRightIcon } from '@/assets/icons/arrow-right-blue-icon.svg';
import { ReactComponent as BoxIcon } from '@/assets/icons/box-outline-icon.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil-blue-icon.svg';
import { ReactComponent as RouteIcon } from '@/assets/icons/route-outline-icon.svg';
import { useDebouncedValue } from '@/shared/lib/hooks/useDebouncedValue';
import AppButton from '@/shared/ui/AppButton';

import { useLocalitiesQuery } from '../../../api/useShippingQueries';
import { AwbFormValues, dimensionsRequired, toNumber } from '../../../model/awbForm';
import {
  DetailLabel,
  DetailValue,
  TariffDivider,
  TariffGrid,
  TariffTotal,
  TariffTotalLabel,
  TariffTotalValue,
} from '../styles';
import AwbSection from './AwbSection';

interface AwbReviewStepProps {
  quote: TariffQuote | undefined;
  quoteLoading: boolean;
  senderPoint: PickupPointView | undefined;
  onEditRoute: () => void;
  onEditParcel: () => void;
}

const money = (value: number | undefined): string =>
  value === undefined || value === null ? '-' : `${value.toFixed(2)} RON`;

const joinParts = (...parts: (string | undefined)[]): string =>
  parts.filter((part) => !!part?.trim()).join(', ');

const TARIFF_SKELETON_SX = { transform: 'none', justifySelf: 'end' };

const TARIFF_AMOUNT_SX = { justifySelf: 'end' };

const HIDDEN_AMOUNT_SX = { ...TARIFF_AMOUNT_SX, visibility: 'hidden' };

const SLOW_QUOTE_MS = 400;

const Detail: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Stack gap="0.5rem" minWidth={0}>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>{children}</DetailValue>
  </Stack>
);

const AwbReviewStep: FC<AwbReviewStepProps> = ({
  quote,
  quoteLoading,
  senderPoint,
  onEditRoute,
  onEditParcel,
}) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<AwbFormValues>();
  const values = getValues();

  const { data: localities = [] } = useLocalitiesQuery(values.county);
  const exteriorKm = useMemo(
    () => localities.find((item) => item.name === values.locality)?.exteriorKm,
    [localities, values.locality],
  );

  const vatPercent =
    quote?.vat && quote.costNoVat ? Math.round((quote.vat / quote.costNoVat) * 100) : undefined;

  const editButton = (onClick: () => void, testId: string) => (
    <AppButton
      variant="secondary"
      size="small"
      startIcon={<PencilIcon />}
      onClick={onClick}
      data-testid={testId}
    >
      {t('Edit')}
    </AppButton>
  );

  const slowLoading = useDebouncedValue(quoteLoading, SLOW_QUOTE_MS);
  const showSkeleton = quoteLoading && slowLoading;

  const tariffRows: { label: string; value?: number }[] = [
    { label: t('awb.tariff_weight'), value: quote?.weightCost },
    {
      label: exteriorKm
        ? t('awb.tariff_extra_km_count', { km: exteriorKm })
        : t('awb.tariff_extra_km'),
      value: quote?.extraKmCost,
    },
    { label: t('awb.tariff_insurance'), value: quote?.insuranceCost },
    { label: t('awb.tariff_fuel'), value: quote?.fuelCost },
    { label: t('awb.tariff_options'), value: quote?.optionsCost },
  ];

  const renderAmount = (value: number | undefined) => {
    if (showSkeleton) {
      return <Skeleton width="3.75rem" height="1.1375rem" sx={TARIFF_SKELETON_SX} />;
    }
    return (
      <DetailValue sx={quoteLoading ? HIDDEN_AMOUNT_SX : TARIFF_AMOUNT_SX}>
        {quoteLoading ? money(0) : money(value)}
      </DetailValue>
    );
  };

  return (
    <Stack direction="row" gap="1rem" alignItems="stretch" data-testid="awb-review">
      <Stack flex={1} minWidth={0} gap="1rem">
        <AwbSection
          icon={<RouteIcon width={24} height={24} />}
          title={t('awb.route')}
          action={editButton(onEditRoute, 'awb-review-edit-route')}
          data-testid="awb-review-route"
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'fit-content(45%) 1.375rem fit-content(45%)',
              justifyContent: 'start',
              columnGap: '4rem',
              rowGap: '0.5rem',
              alignItems: 'center',
            }}
            data-testid="awb-review-route-grid"
          >
            <DetailLabel>{t('awb.route_from')}</DetailLabel>
            <Box display="flex" sx={{ color: (theme) => theme.palette.blue[600] }}>
              <ArrowRightIcon width={22} height={22} />
            </Box>
            <DetailLabel>{t('awb.route_to')}</DetailLabel>

            <DetailValue>{senderPoint?.name ?? senderPoint?.fanClientId ?? '-'}</DetailValue>
            <span />
            <DetailValue>{joinParts(values.recipientName, values.locality) || '-'}</DetailValue>

            <DetailValue>
              {joinParts(
                [senderPoint?.street, senderPoint?.streetNo].filter(Boolean).join(' '),
                senderPoint?.locality,
                senderPoint?.county,
              ) || '-'}
            </DetailValue>
            <span />
            <DetailValue>
              {joinParts(
                [values.street, values.streetNo].filter(Boolean).join(' '),
                values.county,
              ) || '-'}
            </DetailValue>

            <DetailValue>{senderPoint?.phone || '-'}</DetailValue>
            <span />
            <DetailValue>{values.phone || '-'}</DetailValue>
          </Box>
        </AwbSection>

        <AwbSection
          icon={<BoxIcon width={24} height={24} />}
          title={t('awb.step_parcel_service')}
          action={editButton(onEditParcel, 'awb-review-edit-parcel')}
          data-testid="awb-review-parcel"
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              columnGap: '3rem',
              rowGap: '2.125rem',
            }}
          >
            <Detail label={t('awb.review_service')}>{values.service || '-'}</Detail>
            <Detail label={t('Pickup date')}>
              {values.intendedPickupDate
                ? dayjs(values.intendedPickupDate).format('D MMM YYYY')
                : '-'}
            </Detail>
            <Detail label={t('Parcels')}>
              {t('awb.review_parcels', {
                parcels: toNumber(values.parcels) ?? 1,
                envelopes: toNumber(values.envelopes) ?? 0,
              })}
            </Detail>
            <Detail label={t('awb.review_total_weight')}>
              {toNumber(values.weight) !== undefined ? `${toNumber(values.weight)} KG` : '-'}
            </Detail>
            {dimensionsRequired(values) && (
              <Detail label={t('awb.review_dimensions')}>
                {`${toNumber(values.length) ?? '-'} x ${toNumber(values.width) ?? '-'} x ${
                  toNumber(values.height) ?? '-'
                } CM`}
              </Detail>
            )}
          </Box>
        </AwbSection>
      </Stack>

      <AwbSection
        title={t('awb.tariff')}
        sx={{ width: '18.5rem', flexShrink: 0 }}
        data-testid="awb-tariff-breakdown"
      >
        <TariffGrid>
          {tariffRows.map((row) => (
            <Box key={row.label} sx={{ display: 'contents' }}>
              <DetailLabel>{row.label}</DetailLabel>
              {renderAmount(row.value)}
            </Box>
          ))}
          <TariffDivider />
          <DetailLabel>{t('awb.tariff_cost_without_vat')}</DetailLabel>
          {renderAmount(quote?.costNoVat)}
          <DetailLabel>
            {vatPercent ? t('awb.tariff_vat_percent', { percent: vatPercent }) : t('VAT')}
          </DetailLabel>
          {renderAmount(quote?.vat)}
        </TariffGrid>

        <TariffTotal>
          <TariffTotalLabel>{t('Total')}</TariffTotalLabel>
          {showSkeleton ? (
            <Skeleton width="5rem" height="1.4rem" sx={TARIFF_SKELETON_SX} />
          ) : (
            <TariffTotalValue sx={quoteLoading ? { visibility: 'hidden' } : undefined}>
              {quoteLoading ? money(0) : money(quote?.total)}
            </TariffTotalValue>
          )}
        </TariffTotal>
      </AwbSection>
    </Stack>
  );
};

export default AwbReviewStep;
